// UniversalScanner — extracted from App.js during the Trading Hub rebuild
// (M4). Pure relocation, no logic changes.
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, fmtN } from "../lib/appCommon";

// ─── Options Hub Tab ──────────────────────────────────────────────────────────
// ─── Universal Market Scanner ─────────────────────────────────────────────────
function UniversalScanner({ setSelectedOption }) {
  const [scanData, setScanData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [tier, setTier] = useState("small");
  const [tierInfo, setTierInfo] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/scanner/tiers`).then(r => {
      setTierInfo(r.data);
      setTier(r.data.currentTier || "small");
    }).catch(() => {});
    // Load recent scan activity from the orphaned endpoint — now actually consumed
    axios.get(`${API}/scanner/recent-events?limit=5`).then(r => {
      setRecentEvents(r.data?.events || []);
    }).catch(() => {});
  }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/scanner/universal`, { tier });
      setScanData(r.data);
      // Refresh recent events after a new scan completes
      axios.get(`${API}/scanner/recent-events?limit=5`).then(re => {
        setRecentEvents(re.data?.events || []);
      }).catch(() => {});
    } catch {}
    setScanning(false);
  };

  const tierColors = { small: "#f97316", mid: "#3b82f6", large: "#4ade80", mega: "#a78bfa" };
  const tierEmoji = { small: "S", mid: "M", large: "L", mega: "XL" };

  return (
    <div data-testid="scanner-panel">
      {/* Tier Selector */}
      <div className="card" style={{ marginBottom: 14, borderColor: `${tierColors[tier]}30`, background: `${tierColors[tier]}06` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div className="sh" style={{ margin: 0 }}>Universal Market Scanner</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>Hedge fund analytics for every account size. Scans full market by tier.</div>
          </div>
          <button className="btn btn-blue" onClick={runScan} disabled={scanning} data-testid="run-scanner" style={{ padding: "10px 20px", fontSize: 13, fontWeight: 700 }}>
            {scanning ? "Scanning Market..." : "Scan Now"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {["small", "mid", "large", "mega"].map(t => {
            const tierLabel = { small: "Small Cap", mid: "Mid Cap", large: "Large Cap", mega: "Mega Cap" }[t];
            const tierDesc = { small: "$1–$15 · <$500", mid: "$15–$80 · <$5K", large: "$80+ · $5K+", mega: "Elite · $25K+" }[t];
            return (
              <div key={t} className={`ap-card ${tier === t ? "sel-full" : ""}`} style={{ flex: 1, padding: 10, cursor: "pointer",
                borderColor: tier === t ? tierColors[t] : "var(--border)", transition: "all 0.2s" }}
                onClick={() => setTier(t)} data-testid={`tier-${t}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ width: 22, height: 22, borderRadius: 6, background: `${tierColors[t]}20`, color: tierColors[t], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>{tierEmoji[t]}</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{tierLabel}</span>
                </div>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>{tierDesc}</div>
                <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 3 }}>{tierInfo?.tiers?.[t]?.tickers?.slice(0, 5).join(", ")}...</div>
              </div>
            );
          })}
        </div>
        {tierInfo && <div style={{ fontSize: 10, color: "var(--dim)" }}>Account: ${tierInfo.equity} | Auto-detected tier: <span style={{ fontWeight: 600, color: tierColors[tierInfo.currentTier] }}>{tierInfo.currentTier?.toUpperCase()}</span></div>}
      </div>

      {/* Top 3 Plays of the Day */}
      {scanData?.top3?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div className="sh" style={{ margin: 0 }}>Top 3 Plays</div>
            <span className="tag" style={{ fontSize: 8, background: "rgba(250,204,21,0.15)", color: "#facc15" }}>AI RANKED</span>
            <span className="tag" style={{ fontSize: 8, background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}>Ω BRAIN GATED</span>
          </div>
          <div className="grid3" style={{ gap: 10 }}>
            {scanData.top3.map((play, i) => {
              const rankColor = i === 0 ? "#facc15" : i === 1 ? "#c0c0c0" : "#cd7f32";
              const rgPassed = play.riskGate?.passed;
              const od = play.omegaDecision;
              return (
                <div key={i} className="card dd-card" style={{ cursor: "pointer",
                  borderColor: i === 0 ? "rgba(250,204,21,0.3)" : i === 1 ? "rgba(192,192,192,0.25)" : "rgba(205,127,50,0.25)",
                  background: i === 0 ? "rgba(250,204,21,0.04)" : "transparent" }}
                  onClick={() => setSelectedOption({ symbol: play.ticker, strike: play.strike, type: play.optionType, expiry: play.expiry, underlying: play.price, bid: play.bid, ask: play.ask, osi: play.osi, volume: play.volume, oi: play.oi })}
                  data-testid={`top-play-${i}`}>
                  {/* Header row */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: rankColor }}>#{i + 1}</span>
                      <span style={{ fontSize: 15, fontWeight: 700 }}>{play.ticker}</span>
                      <span className={`tag ${play.ziClass === "ULTRA_GEM" ? "t-bull" : play.ziClass === "GEM" ? "t-blue" : "t-neu"}`} style={{ fontSize: 8 }}>{play.ziClass?.replace("_", " ")}</span>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {play.ziSignal?.bias && <span className={`tag ${play.ziSignal.bias === "BULLISH" ? "t-bull" : play.ziSignal.bias === "BEARISH" ? "t-bear" : "t-neu"}`} style={{ fontSize: 7 }}>Ω {play.ziSignal.bias}</span>}
                      <span style={{ fontSize: 8, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: rgPassed ? "rgba(74,222,128,0.15)" : "rgba(251,191,36,0.15)", color: rgPassed ? "#4ade80" : "#fbbf24" }}>
                        {rgPassed ? "✓ AUTO OK" : "⚠ REVIEW"}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 8 }}>${play.strike} {play.optionType?.toUpperCase()} · exp {play.expiry} · Stock ${play.price}</div>
                  {/* Omega Brain verdict bar */}
                  {od && (
                    <div style={{ padding: "7px 10px", borderRadius: 8, background: "rgba(167,139,250,0.07)", border: "1px solid rgba(167,139,250,0.18)", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontSize: 8, color: "#a78bfa", fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Omega Brain</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: od.verdict === "BUY" ? "#4ade80" : od.verdict === "HOLD" ? "#fbbf24" : "#f87171" }}>{od.verdict || od.status}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 8, color: "var(--dim)", marginBottom: 1 }}>Kelly size</div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>${fmtN(od.kellyDollars, 0)} · {od.kellyContracts}ct</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 8, color: "var(--dim)", marginBottom: 1 }}>Meta conf</div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: (od.metaConfidence || 0) >= 0.8 ? "#4ade80" : (od.metaConfidence || 0) >= 0.6 ? "#fbbf24" : "#f87171" }}>{((od.metaConfidence || 0) * 100).toFixed(0)}%</div>
                      </div>
                    </div>
                  )}
                  {/* Stats grid */}
                  <div className="grid3" style={{ gap: 4, marginBottom: 6 }}>
                    <div><div style={{ fontSize: 9, color: "var(--dim)" }}>Zi Score</div><div style={{ fontSize: 16, fontWeight: 700, color: play.ziScore >= 63 ? "var(--green)" : "var(--blue)" }}>{play.ziScore}</div></div>
                    <div><div style={{ fontSize: 9, color: "var(--dim)" }}>MC PoP</div><div style={{ fontSize: 14, fontWeight: 600, color: play.mcPoP >= 60 ? "var(--green)" : "var(--muted)" }}>{play.mcPoP}%</div></div>
                    <div><div style={{ fontSize: 9, color: "var(--dim)" }}>Max ROI</div><div style={{ fontSize: 14, fontWeight: 600, color: "var(--green)" }}>{play.maxROI}%</div></div>
                  </div>
                  <div style={{ display: "flex", gap: 8, fontSize: 9, color: "var(--dim)", flexWrap: "wrap" }}>
                    <span>Cost: ${play.contractCost}</span>
                    <span>IV: {play.ivRank}%</span>
                    <span>Vol: {play.volume?.toLocaleString()}</span>
                    <span>DTE: {play.dte}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full Results Table */}
      {scanData && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div className="sh" style={{ margin: 0 }}>All Opportunities ({scanData.totalOpportunities})</div>
            <span style={{ fontSize: 10, color: "var(--dim)" }}>{scanData.tickersScanned} tickers scanned | {scanData.tierLabel} tier</span>
          </div>
          {scanData.results?.length > 0 ? (
            <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
              <table className="tbl" style={{ fontSize: 10 }}>
                <thead><tr>
                  <th style={{ textAlign: "left" }}>#</th><th style={{ textAlign: "left" }}>Ticker</th><th>$</th><th>Strike</th><th>Type</th>
                  <th>Zi</th><th>Class</th><th>Gate</th><th>Verdict</th><th>Kelly $</th><th>MC PoP</th><th>ROI</th><th>Cost</th><th>IV</th><th>Vol</th><th>DTE</th>
                </tr></thead>
                <tbody data-testid="scanner-results">
                  {scanData.results.map((r, i) => {
                    const rgPassed = r.riskGate?.passed;
                    const od = r.omegaDecision;
                    return (
                    <tr key={i} style={{ cursor: "pointer", background: i < 3 ? "rgba(250,204,21,0.03)" : r.ziClass === "ULTRA_GEM" ? "rgba(74,222,128,0.03)" : "transparent" }}
                      onClick={() => setSelectedOption({ symbol: r.ticker, strike: r.strike, type: r.optionType, expiry: r.expiry, underlying: r.price, bid: r.bid, ask: r.ask, osi: r.osi, volume: r.volume, oi: r.oi })}>
                      <td style={{ textAlign: "left", fontWeight: 600, color: i < 3 ? "#facc15" : "var(--muted)" }}>{i + 1}</td>
                      <td style={{ textAlign: "left", fontWeight: 700 }}>{r.ticker}</td>
                      <td>${r.price}</td>
                      <td>${r.strike}</td>
                      <td>{r.optionType?.charAt(0).toUpperCase()}</td>
                      <td style={{ fontWeight: 700, fontSize: 12, color: r.ziScore >= 68 ? "var(--green)" : r.ziScore >= 53 ? "var(--blue)" : "var(--yellow)" }}>{r.ziScore}</td>
                      <td>
                        <span className={`tag ${r.ziClass === "ULTRA_GEM" ? "t-bull" : r.ziClass === "GEM" ? "t-blue" : "t-neu"}`} style={{ fontSize: 7 }}>{r.ziClass?.replace("_", " ")}</span>
                        {r.ziSignal?.bias && <span className={`tag ${r.ziSignal.bias === "BULLISH" ? "t-bull" : r.ziSignal.bias === "BEARISH" ? "t-bear" : "t-neu"}`} style={{ fontSize: 7, marginLeft: 3 }}>Ω {r.ziSignal.bias}</span>}
                      </td>
                      <td>
                        <span style={{ fontSize: 8, fontWeight: 700, padding: "2px 5px", borderRadius: 4, background: rgPassed ? "rgba(74,222,128,0.15)" : "rgba(251,191,36,0.15)", color: rgPassed ? "#4ade80" : "#fbbf24" }}>
                          {rgPassed === undefined ? "–" : rgPassed ? "✓" : "⚠"}
                        </span>
                      </td>
                      <td style={{ fontWeight: 700, fontSize: 10, color: od?.verdict === "BUY" ? "#4ade80" : od?.verdict === "HOLD" ? "#fbbf24" : od?.verdict === "AVOID" ? "#f87171" : "var(--dim)" }}>
                        {od?.verdict || "–"}
                      </td>
                      <td style={{ fontSize: 10, color: "var(--text)" }}>{od?.kellyDollars ? `$${fmtN(od.kellyDollars, 0)}` : "–"}</td>
                      <td style={{ color: r.mcPoP > 60 ? "var(--green)" : "var(--muted)" }}>{r.mcPoP}%</td>
                      <td style={{ fontWeight: 600, color: "var(--green)" }}>{r.maxROI}%</td>
                      <td>${r.contractCost}</td>
                      <td>{r.ivRank}%</td>
                      <td>{r.volume?.toLocaleString()}</td>
                      <td>{r.dte}d</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: "var(--muted)", fontSize: 12, padding: 16 }}>{scanData.totalOpportunities === 0 ? "No opportunities matched the filters for this tier. Market may be closed or filters too strict." : "Scan complete."}</div>
          )}
          {scanData.errors?.length > 0 && <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 6 }}>Errors: {scanData.errors.join(" | ")}</div>}
        </div>
      )}

      {!scanData && !scanning && (
        <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Ready to Scan the Market</div>
          <div style={{ fontSize: 12, color: "var(--muted)", maxWidth: 400, margin: "0 auto" }}>
            Select your tier and click "Scan Now" to find the best option contracts for your account size. The scanner evaluates IV, volume, momentum, and Zi Score across {tierInfo?.tiers?.[tier]?.tickers?.length || 20} stocks.
          </div>
        </div>
      )}

      {/* Recent Scan Activity — Scanner Recent Events wired here */}
      {recentEvents.length > 0 && (
        <div className="card" style={{ marginTop: 14, borderColor: "rgba(167,139,250,0.15)", background: "rgba(167,139,250,0.03)" }}>
          <div className="sh" style={{ margin: "0 0 10px", fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
            Recent Scan Activity
            <span className="tag t-neu" style={{ fontSize: 7 }}>AUTO + MANUAL</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {recentEvents.map((ev, i) => {
              const ts = ev.ts ? new Date(ev.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
              const date = ev.ts ? new Date(ev.ts).toLocaleDateString([], { month: "short", day: "numeric" }) : "";
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "6px 10px", borderRadius: 8, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa", flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: "var(--text)", fontWeight: 500 }}>{ev.message}</span>
                    {ev.totalOps > 0 && <span style={{ fontSize: 9, color: "var(--dim)" }}>· {ev.totalOps} ops</span>}
                    {ev.tier && <span className="tag t-neu" style={{ fontSize: 7 }}>{ev.tier.toUpperCase()}</span>}
                  </div>
                  <span style={{ fontSize: 9, color: "var(--dim)", whiteSpace: "nowrap" }}>{date} {ts}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default UniversalScanner;
